/**
 * EditingOptions types - Export-only options applied when generating DOCX/HWPX files
 * Mirrors desktop app's "편집 옵션" dialog in modules/export_questions.py
 */

/**
 * 교재 옵션 (Textbook Options) - Settings for textbook-style exports
 */
export interface TextbookOptions {
  // 지문분석 settings
  jimunAnalysisFont: string;
  jimunAnalysisFontSize: number;
  jimunAnalysisFontColor: string;
  jimunAnalysisLineSpacing: number;
  jimunAnalysisSentenceSplit: boolean; // Split passage by sentences (at circled numbers)
  jimunAnalysisInterpretationRight: boolean; // Place interpretation on right side (two-column table)

  // 한글해석 settings (for right column when interpretationRight=true)
  hanjulHaesukFont: string;
  hanjulHaesukFontSize: number;
  hanjulHaesukFontColor: string;
  hanjulHaesukLineSpacing: number;

  // 한줄해석연습 settings
  hanjulInterpretationIncludeAnswer: boolean;

  // 한줄영작연습 settings
  hanjulWritingIncludeAnswer: boolean;
  hanjulWritingWordScramble: boolean;
  hanjulWritingWordFormChange: boolean;
  hanjulWritingKoreanOnly: boolean;
}

/**
 * 문제 옵션 (Question Export Options) - Settings for question export
 */
export interface QuestionExportOptions {
  naeyongilchiQuestionType: 0 | 1; // 0=T/F 형식, 1=5지선다
}

/**
 * Combined editing options
 */
export interface EditingOptions {
  textbook: TextbookOptions;
  question: QuestionExportOptions;
}

/** Bounds the stored textbook font values are clamped to; the editor inputs use the same. */
export const TEXTBOOK_FONT_LIMITS = {
  SIZE_MIN: 6,
  SIZE_MAX: 20,
  LINE_SPACING_MIN: 0.5,
  LINE_SPACING_MAX: 3,
} as const;

/**
 * Default values matching desktop app (modules/converter.py:375-438)
 */
export const DEFAULT_TEXTBOOK_OPTIONS: TextbookOptions = {
  // 지문분석 defaults
  jimunAnalysisFont: '나눔고딕',
  jimunAnalysisFontSize: 8,
  jimunAnalysisFontColor: '#000000',
  jimunAnalysisLineSpacing: 1.2,
  jimunAnalysisSentenceSplit: false,
  jimunAnalysisInterpretationRight: false,

  // 한글해석 defaults
  hanjulHaesukFont: '나눔고딕',
  hanjulHaesukFontSize: 6,
  hanjulHaesukFontColor: '#000000',
  hanjulHaesukLineSpacing: 1,

  // 한줄해석연습 defaults
  hanjulInterpretationIncludeAnswer: true,

  // 한줄영작연습 defaults
  hanjulWritingIncludeAnswer: true,
  hanjulWritingWordScramble: true,
  hanjulWritingWordFormChange: false,
  hanjulWritingKoreanOnly: false,
};

export const DEFAULT_QUESTION_EXPORT_OPTIONS: QuestionExportOptions = {
  naeyongilchiQuestionType: 0, // T/F 형식
};

export const DEFAULT_EDITING_OPTIONS: EditingOptions = {
  textbook: DEFAULT_TEXTBOOK_OPTIONS,
  question: DEFAULT_QUESTION_EXPORT_OPTIONS,
};

/**
 * Storage key for localStorage
 */
export const EDITING_OPTIONS_STORAGE_KEY = 'autoqm-editing-options-v1';

/**
 * Helper to convert camelCase frontend keys to snake_case for backend wire format
 */
export function convertEditingOptionsToWireFormat(options: EditingOptions): {
  textbook: Record<string, unknown>;
  question: Record<string, unknown>;
} {
  return {
    textbook: {
      jimun_analysis_font: options.textbook.jimunAnalysisFont,
      jimun_analysis_font_size: options.textbook.jimunAnalysisFontSize,
      jimun_analysis_font_color: options.textbook.jimunAnalysisFontColor,
      jimun_analysis_line_spacing: options.textbook.jimunAnalysisLineSpacing,
      jimun_analysis_sentence_split: options.textbook.jimunAnalysisSentenceSplit,
      jimun_analysis_interpretation_right: options.textbook.jimunAnalysisInterpretationRight,
      hanjul_haesuk_font: options.textbook.hanjulHaesukFont,
      hanjul_haesuk_font_size: options.textbook.hanjulHaesukFontSize,
      hanjul_haesuk_font_color: options.textbook.hanjulHaesukFontColor,
      hanjul_haesuk_line_spacing: options.textbook.hanjulHaesukLineSpacing,
      hanjul_interpretation_include_answer: options.textbook.hanjulInterpretationIncludeAnswer,
      hanjul_writing_include_answer: options.textbook.hanjulWritingIncludeAnswer,
      hanjul_writing_word_scramble: options.textbook.hanjulWritingWordScramble,
      hanjul_writing_word_form_change: options.textbook.hanjulWritingWordFormChange,
      hanjul_writing_korean_only: options.textbook.hanjulWritingKoreanOnly,
    },
    question: {
      naeyongilchi_question_type: options.question.naeyongilchiQuestionType,
    },
  };
}

/**
 * Helper to check if textbook options differ from defaults
 */
export function isTextbookOptionsModified(options: TextbookOptions): boolean {
  return (
    options.jimunAnalysisFont !== DEFAULT_TEXTBOOK_OPTIONS.jimunAnalysisFont ||
    options.jimunAnalysisFontSize !== DEFAULT_TEXTBOOK_OPTIONS.jimunAnalysisFontSize ||
    options.jimunAnalysisFontColor !== DEFAULT_TEXTBOOK_OPTIONS.jimunAnalysisFontColor ||
    options.jimunAnalysisLineSpacing !== DEFAULT_TEXTBOOK_OPTIONS.jimunAnalysisLineSpacing ||
    options.jimunAnalysisSentenceSplit !== DEFAULT_TEXTBOOK_OPTIONS.jimunAnalysisSentenceSplit ||
    options.jimunAnalysisInterpretationRight !== DEFAULT_TEXTBOOK_OPTIONS.jimunAnalysisInterpretationRight ||
    options.hanjulHaesukFont !== DEFAULT_TEXTBOOK_OPTIONS.hanjulHaesukFont ||
    options.hanjulHaesukFontSize !== DEFAULT_TEXTBOOK_OPTIONS.hanjulHaesukFontSize ||
    options.hanjulHaesukFontColor !== DEFAULT_TEXTBOOK_OPTIONS.hanjulHaesukFontColor ||
    options.hanjulHaesukLineSpacing !== DEFAULT_TEXTBOOK_OPTIONS.hanjulHaesukLineSpacing ||
    options.hanjulInterpretationIncludeAnswer !== DEFAULT_TEXTBOOK_OPTIONS.hanjulInterpretationIncludeAnswer ||
    options.hanjulWritingIncludeAnswer !== DEFAULT_TEXTBOOK_OPTIONS.hanjulWritingIncludeAnswer ||
    options.hanjulWritingWordScramble !== DEFAULT_TEXTBOOK_OPTIONS.hanjulWritingWordScramble ||
    options.hanjulWritingWordFormChange !== DEFAULT_TEXTBOOK_OPTIONS.hanjulWritingWordFormChange ||
    options.hanjulWritingKoreanOnly !== DEFAULT_TEXTBOOK_OPTIONS.hanjulWritingKoreanOnly
  );
}

/**
 * Helper to check if question export options differ from defaults
 */
export function isQuestionExportOptionsModified(options: QuestionExportOptions): boolean {
  return options.naeyongilchiQuestionType !== DEFAULT_QUESTION_EXPORT_OPTIONS.naeyongilchiQuestionType;
}
